import { Box, SelectChangeEvent } from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import TextInput from '../../../../components/TextInput/TextInput';
import Dropdown from '../../../../components/Dropdown/Dropdown';

import { EmailRegex, NameRegex } from '../../../../configs/RegexConfig';

import useNewUser from './useNewUser';

interface NewUserCreateUserStepProps {
  onClose: () => void;
  onNewUserSuccess: () => void;
}
function NewUserCreateUserStep({ onClose, onNewUserSuccess }: NewUserCreateUserStepProps) {
  const [t] = useTranslation();
  const { isLoading, userExists, companyOptions, banOptions, updateBanOptions } = useNewUser(onClose, onNewUserSuccess);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      companyId: '',
      billingAccountBan: '',
      username: '',
      companyRole: '',
      department: '',
    },
    validateOnMount: true,
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: yup.object({
      firstName: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NameRegex,
          t('common.inputValidations.validField', {
            field: t(`pages.eSimManager.newUserDrawer.firstName`).toLowerCase(),
          }),
        ),
      lastName: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NameRegex,
          t('common.inputValidations.validField', {
            field: t(`pages.eSimManager.newUserDrawer.lastName`).toLowerCase(),
          }),
        ),
      email: yup
        .string()
        .matches(EmailRegex, t('common.inputValidations.email'))
        .required(t('common.inputValidations.requiredField')),
      companyId: yup.string().required(t('common.inputValidations.requiredField')),
      billingAccountBan: yup.string().required(t('common.inputValidations.requiredField')),
      username: yup.string(),
      companyRole: yup.string(),
      department: yup.string(),
    }),
    onSubmit: (values) => {
      userExists(
        { email: values.email, username: values.username },
        {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          billingAccountBan: values.billingAccountBan,
          username: values.username,
          companyRole: values.companyRole,
          department: values.department,
        },
      );
    },
  });

  useEffect(() => {
    if (companyOptions.length === 1 && !formik.values.companyId) {
      const companyId = companyOptions[0].value as string;
      formik.setFieldValue('companyId', companyId);

      const bans = updateBanOptions(companyId);

      if (bans.length === 1) {
        formik.setFieldValue('billingAccountBan', bans[0].value);
      }
    }
  }, [companyOptions]);

  /**
   * Handle company change - update ban options
   */
  const handleCompanyChange = async (e: SelectChangeEvent<unknown>) => {
    formik.handleChange(e);
    const bans = updateBanOptions(e.target.value as string);

    formik.setFieldValue('billingAccountBan', '');
    formik.setFieldTouched('billingAccountBan', false);

    if (bans.length === 1) {
      formik.setFieldValue('billingAccountBan', bans[0].value);
    }

    await formik.validateField('companyId');
    await formik.validateField('billingAccountBan');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ paddingBottom: '2rem' }}>
        <form onSubmit={formik.handleSubmit} style={{ paddingTop: '1.5rem' }}>
          <TextInput
            autoComplete="off"
            key="firstName"
            id="firstName"
            name="firstName"
            type="text"
            label={t(`pages.eSimManager.newUserDrawer.firstName`)}
            value={formik.values.firstName}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            feedback={formik.touched.firstName && Boolean(formik.errors.firstName) ? formik.errors.firstName : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.eSimManager.newUserDrawer.firstName`).toLowerCase()
            }
          />
          <TextInput
            autoComplete="off"
            key="lastName"
            id="lastName"
            name="lastName"
            type="text"
            label={t(`pages.eSimManager.newUserDrawer.lastName`)}
            value={formik.values.lastName}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            feedback={formik.touched.lastName && Boolean(formik.errors.lastName) ? formik.errors.lastName : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.eSimManager.newUserDrawer.lastName`).toLowerCase()
            }
          />
          <TextInput
            autoComplete="off"
            key="username"
            id="username"
            name="username"
            type="text"
            label={t(`pages.eSimManager.newUserDrawer.username`)}
            value={formik.values.username}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.username && Boolean(formik.errors.username)}
            feedback={formik.touched.username && Boolean(formik.errors.username) ? formik.errors.username : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.eSimManager.newUserDrawer.username`).toLowerCase()
            }
          />
          <TextInput
            autoComplete="off"
            key="email"
            id="email"
            name="email"
            type="email"
            label={t(`pages.eSimManager.newUserDrawer.email`)}
            value={formik.values.email}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            feedback={formik.touched.email && Boolean(formik.errors.email) ? formik.errors.email : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.eSimManager.newUserDrawer.email`).toLowerCase()
            }
          />
          <Dropdown
            id="companyId"
            name="companyId"
            label={t('pages.eSimManager.newUserDrawer.company')}
            value={formik.values.companyId}
            onChange={handleCompanyChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyId && Boolean(formik.errors.companyId)}
            feedback={formik.touched.companyId && Boolean(formik.errors.companyId) ? formik.errors.companyId : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            items={companyOptions}
            disabled={companyOptions.length === 1}
            placeholder={
              t('common.placeholders.dropdownPlaceholder') + t('pages.eSimManager.newUserDrawer.company').toLowerCase()
            }
          />
          <Dropdown
            id="billingAccountBan"
            name="billingAccountBan"
            label={t('pages.eSimManager.newUserDrawer.ban')}
            value={formik.values.billingAccountBan}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.billingAccountBan && Boolean(formik.errors.billingAccountBan)}
            feedback={
              formik.touched.billingAccountBan && Boolean(formik.errors.billingAccountBan)
                ? formik.errors.billingAccountBan
                : ''
            }
            formStyle={{ paddingBottom: '1.5rem' }}
            items={banOptions}
            disabled={banOptions.length === 1 || !formik.values.companyId}
            placeholder={
              t('common.placeholders.dropdownPlaceholder') + t('pages.eSimManager.newUserDrawer.ban').toLowerCase()
            }
          />
          <TextInput
            autoComplete="off"
            key="companyRole"
            id="companyRole"
            name="companyRole"
            type="text"
            label={t(`pages.eSimManager.newUserDrawer.role`)}
            value={formik.values.companyRole}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyRole && Boolean(formik.errors.companyRole)}
            feedback={formik.touched.companyRole && Boolean(formik.errors.companyRole) ? formik.errors.companyRole : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.eSimManager.newUserDrawer.role`).toLowerCase()
            }
          />
          <TextInput
            autoComplete="off"
            key="department"
            id="department"
            name="department"
            type="text"
            label={t(`pages.eSimManager.newUserDrawer.department`)}
            value={formik.values.department}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.department && Boolean(formik.errors.department)}
            feedback={formik.touched.department && Boolean(formik.errors.department) ? formik.errors.department : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.eSimManager.newUserDrawer.department`).toLowerCase()
            }
          />
        </form>
      </Box>
      <Box sx={{ marginTop: 'auto', paddingBottom: '2rem' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} disabled={isLoading} />
          <Button
            label={t('pages.eSimManager.newUserDrawer.addUser')}
            color="primaryButton"
            disabled={!formik.isValid || isLoading || sessionStorage.getItem('impersonateId') !== null}
            type="submit"
            onClick={formik.submitForm}
          />
        </ButtonGroup>
      </Box>
    </Box>
  );
}

export default NewUserCreateUserStep;
